<template>
  <v-data-table v-model:expanded="expanded"
                v-model:sort-by="sortBy"
                :headers="headers"
                :items="filteredVirtualPanels"
                :items-per-page="15" :row-props="rowProps"
                @click:row="handleRowClicked"
                show-expand>
    <template v-slot:top>
      <v-toolbar color="secondary" flat>
        <v-toolbar-title class="text-button">Virtual Expert Panels</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" max-width="50%">
          <template v-slot:activator="{ props }">
            <v-btn color="primary" dark class="mb-2" v-bind="props"
                   v-if="this.initialized && hasAccessRight('virtual-panel-admin')">New Item</v-btn>
          </template>
          <v-card>
            <v-form lazy-validation @submit.prevent="validateAndSave" ref="virtual_panel_form" :disabled="dialogDisabled">
              <v-card-title>
                <span class="text-h5">{{ formTitle }}</span>
              </v-card-title>
              <v-card-text class="error" v-if="failedSave">{{failedSave}}</v-card-text>
              <v-card-text class="error" v-if="failedAddNewParticipant">Failed to add new participant! Please check that you have entered a valid email address.</v-card-text>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="4">
                      <v-select v-model="editedItem.group" :rules="rules.group" :items="groupItems"
                                  :item-title="(item) => item.name"
                                  :item-value="(item) => item"
                                  label="Owner company" :disabled="editedIndex !== -1">
                      </v-select>
                    </v-col>
                    <v-col cols="4">
                      <v-select v-model="editedItem.customer" :rules="rules.customer" :items="customerItems"
                                  :item-title="(item) => item.name"
                                  :item-value="(item) => item"
                                  label="Customer" :disabled="editedIndex !== -1">
                      </v-select>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field v-model="editedItem.title" :rules="rules.title"
                                    label="Virtual expert panel name"></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4">
                      <v-number-input v-model="editedItem.duration_in_hours"
                                      :reverse="false"
                                      controlVariant="default"
                                      label="Duration in hours"
                                      :hideInput="false"
                                      :inset="false"
                                      :rules="rules.duration_in_hours"></v-number-input>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field v-model="editedItem.approval_code"
                                    label="Compliance Approval Code"
                                    placeholder="Enter compliance approval code">
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-textarea v-model="editedItem.details"
                                  label="Additional panel information"
                                  placeholder="Enter additional panel information"
                                  hint="Confidentiality or other text information, e.g., registered address for the client."
                                  rows="2"
                                  variant="filled"
                                  auto-grow shaped persistent-hint></v-textarea>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-file-input v-model="editedItem.files" label="Additional panel files"
                                    placeholder="Upload your documents" prepend-icon="mdi-paperclip"
                                    :multiple="true">
                        <template v-slot:selection="{ fileNames }">
                          <template v-for="fileName in fileNames" :key="fileName">
                            <v-chip class="me-2" color="primary" size="small" label>
                              {{ fileName }}
                            </v-chip>
                          </template>
                        </template>
                      </v-file-input>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-autocomplete v-model="editedItem.participants"
                                      v-model:menu="participantMenu"
                                      :rules="rules.participants"
                                      :items="participantItems"
                                      v-model:search="participantSearch"
                                      @keydown.enter.prevent.stop.capture="newParticipantItem()"
                                      @update:search="failedAddNewParticipant = false"
                                      :item-title="(item) => formatParticipant(item)"
                                      :item-value="(item) => item"
                                      label="Participants" placeholder="Select participants"
                                      hint="Select from previously used participants or enter a new email address.
                                      No invitation to participate in the panel is emailed until the virtual expert panel is started."
                                      chips multiple persistent-hint hide-no-data clear-on-select>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-data-iterator :items="editedItem.questions" :page="editedItem.questionsPage" :items-per-page="2">
                    <template v-slot:header="{ pageCount, setPage }">
                      <v-toolbar color="secondary" flat>
                        <v-spacer></v-spacer>
                        <v-btn variant="plain" @click="addQuestion(() => setPage(pageCount + 1))">Add question</v-btn>
                      </v-toolbar>
                    </template>
                    <template v-slot:default="{ page, itemsPerPage, pageCount, items }">
                      <template v-for="(item, i) in items" :key="i">
                        <v-card :title="`Question ${computeQuestionIndex(page, itemsPerPage, i) + 1}`">
                          <v-card-text>
                            <div v-for="(questionLine, i) in item.raw.question?.split('\n')" :key="i">
                              <p v-if="questionLine">{{questionLine}}</p>
                              <br v-else>
                            </div>
                          </v-card-text>
                          <template v-slot:actions>
                            <v-icon size="small" class="me-2"
                                    v-tooltip="'Move question up'"
                                    @click="moveQuestionUp(computeQuestionIndex(page, itemsPerPage, i))"
                                    :disabled="(page === 1) && (i === 0)">mdi-arrow-up</v-icon>
                            <v-icon size="small" class="me-2"
                                    v-tooltip="'Move question down'"
                                    @click="moveQuestionDown(computeQuestionIndex(page, itemsPerPage, i))"
                                    :disabled="(page >= pageCount) && (i === (items.length - 1))">mdi-arrow-down</v-icon>
                            <v-spacer></v-spacer>
                            <div class="ml-2 text-caption text-primary-darken-1">Number of characters in answers: {{item.raw.minimum_answer_length}} - {{item.raw.maximum_answer_length}}</div>
                            <v-spacer></v-spacer>
                            <v-icon size="small" class="me-2"
                                    v-tooltip="'Edit question'"
                                    @click="editQuestion(item.raw)">mdi-pencil</v-icon>
                            <v-icon size="small" class="me-2" v-tooltip="'Delete question'"
                                    @click="editedItem.questions.splice(computeQuestionIndex(page, itemsPerPage, i), 1)">mdi-delete</v-icon>
                          </template>
                        </v-card>
                        <br>
                      </template>
                    </template>
                    <template v-slot:footer="{ page, itemsPerPage, pageCount, prevPage, nextPage, items }">
                      <div class="d-flex align-center justify-center pa-4">
                        <v-btn :disabled="page === 1" density="comfortable" icon="mdi-arrow-left"
                               variant="tonal" rounded @click="prevPage"></v-btn>
                        <div class="mx-2 text-caption" v-if="items.length > 1">
                          Questions {{ (page - 1) * itemsPerPage + 1 }} - {{ (page - 1) * itemsPerPage + items.length }} of {{ editedItem.questions.length }}
                        </div>
                        <div class="mx-2 text-caption" v-else-if="items.length > 0">
                          Question {{ (page - 1) * itemsPerPage + 1 }} of {{ editedItem.questions.length }}
                        </div>
                        <div class="mx-2 text-caption" v-else>
                          No questions
                        </div>
                        <v-btn :disabled="page >= pageCount" density="comfortable" icon="mdi-arrow-right"
                               variant="tonal" rounded @click="nextPage"></v-btn>
                      </div>
                    </template>
                  </v-data-iterator>
                  <v-row>
                    <v-col cols="12">
                      <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn variant="text" @click="close" :disabled="dialogDisabled">Cancel</v-btn>
                <v-btn type="submit" variant="text" :disabled="dialogDisabled">Save</v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogView" max-width="50%">
          <v-card :style="renderCustomerStyle(editedItem.customer)">
            <v-card-title>
              <v-row align="center" justify="start">
                <v-col cols="6" sm="6" md="8">
                  <div class="text-h5 ms-5 text-wrap">Panel: {{ editedItem.title }}</div>
                </v-col>
                <v-col cols="6" sm="6" md="4" class="d-flex">
                  <v-spacer/>
                  <v-img :src="getCustomerLogoFile(editedItem.customer)" class="mt-5 me-5"
                         min-height="25px" max-height="50px"
                         v-if="getCustomerLogoFile(editedItem.customer)"/>
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text class="error" v-if="failedClone">{{failedClone}}</v-card-text>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field label="Created at" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ this.formatTimestamp(editedItem.created_date_time) }}
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field label="Started or resumed at" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ this.formatTimestamp(editedItem.started_date_time) }}
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field label="Completed at" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ this.formatTimestamp(editedItem.completed_date_time) }}
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field label="Duration" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ this.formatDuration(editedItem.duration_in_hours) }}
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field label="Total running time" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ this.formatTotalRunningTime(editedItem.total_running_time_in_seconds_dynamic) }}
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field label="Compliance approval code" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ editedItem.approval_code }}
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field label="Status" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ this.formatStatus(editedItem.status) }}
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <v-text-field label="Time left" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      <div v-html="renderTimeLeft(editedItem.time_left_in_seconds)"></div>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4" v-if="isCurrentUserParticipant(editedItem)">
                    <v-text-field label="Valid answers" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      <div :class="allAnswersValid(editedItem.questions) ? 'text-success' : 'text-warning'">
                        {{ computeValidAnswers(editedItem.questions) }} / {{ editedItem.questions.length }}
                      </div>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea v-model="editedItem.details"
                                label="Additional panel information"
                                rows="2"
                                :readonly="true" :focused="true" variant="plain"
                                @update:focused="() => {}"
                                auto-grow shaped></v-textarea>
                  </v-col>
                  <v-col cols="12">
                    <v-item-group multiple>
                      <div class="text-caption mb-2">Additional panel files</div>
                      <v-item v-for="file in editedItem.files" :key="file">
                        <v-chip @click.stop="downloadPanelFile(editedItem.id, file.name, file.type)">
                          {{ file.name }}
                        </v-chip>
                      </v-item>
                    </v-item-group>
                  </v-col>
                </v-row>
                <v-data-iterator :items="editedItem.questions" :page="editedItem.questionsPage" :items-per-page="2">
                  <template v-slot:header>
                    <v-toolbar :color="getCustomerBackgroundColor(editedItem.customer) || 'secondary'" flat>
                      <v-label>Questions:</v-label>
                      <v-spacer></v-spacer>
                    </v-toolbar>
                  </template>
                  <template v-slot:default="{ page, itemsPerPage, items }">
                    <template v-for="(item, i) in items" :key="i">
                      <v-card :title="`Question ${computeQuestionIndex(page, itemsPerPage, i) + 1}`"
                              :style="renderCustomerStyle(editedItem.customer)">
                        <v-card-text>
                          <div v-for="(questionLine, i) in item.raw.question?.split('\n')" :key="i">
                            <p v-if="questionLine">{{questionLine}}</p>
                            <br v-else>
                          </div>
                        </v-card-text>
                        <template v-slot:actions>
                          <div class="ml-2 text-caption text-success" v-if="isValidAnswer(item.raw)">
                            {{ item.raw.answer?.length }}
                          </div>
                          <div class="ml-2 text-caption text-warning" v-else>
                            {{ item.raw.answer?.length }}
                          </div>
                          <v-spacer></v-spacer>
                          <div class="ml-2 text-caption error" v-if="item.raw.failedSaveAnswer">
                            {{ item.raw.failedSaveAnswer}}
                          </div>
                          <div class="ml-2 text-caption text-primary-darken-1" v-else>
                            Required number of characters in answer: {{item.raw.minimum_answer_length}} - {{item.raw.maximum_answer_length}}
                          </div>
                          <v-spacer></v-spacer>
                          <div v-if="isCurrentUserParticipant(editedItem)">
                            <span>
                              <v-progress-circular size="small" color="primary" :indeterminate="item.raw.savingAnswer"></v-progress-circular>
                            </span>
                            <span class="ml-2 text-caption text-primary-darken-1" v-if="item.raw.savingAnswer">Saving</span>
                            <span class="ml-2 text-caption error" v-else-if="item.raw.failedSaveAnswer">Failed</span>
                            <span class="ml-2 text-caption text-primary-darken-1" v-else>Saved</span>
                          </div>
                        </template>
                        <v-textarea v-model="item.raw.answer"
                                    @update:model-value="() => answerUpdated(editedItem.id, item.raw)"
                                    :rules="rules.answer"
                                    label="My answer"
                                    rows="2"
                                    variant="filled"
                                    :disabled="dialogDisabled || (editedItem.status !== 'STARTED') || !this.ownProfileComplete
                                    || !this.currentUserContracted(editedItem.group.id, editedItem.customer.id)"
                                    v-if="isCurrentUserParticipant(editedItem)"
                                    auto-grow shaped></v-textarea>
                        <v-file-input v-model="item.raw.answerFile"
                                      @update:model-value="() => answerUpdated(editedItem.id, item.raw)"
                                      label="Supporting or reference materials"
                                      placeholder="Upload your document"
                                      hint="Whenever you are uploading information, please ensure that you have permission to do so."
                                      prepend-icon="mdi-paperclip"
                                      accept=".bmp,image/bmp,
                                      .csv,text/csv,
                                      .doc,application/msword,
                                      .docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                      .gif,image/gif,
                                      .jpeg,.jpg,image/jpeg,
                                      .png,image/png,
                                      .pdf,application/pdf,
                                      .ppt,application/vnd.ms-powerpoint,
                                      .pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation,
                                      .svg,image/svg+xml,
                                      .txt,text/plain,
                                      .tif,.tiff,image/tiff,
                                      .xls,application/vnd.ms-excel,
                                      .xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                      variant="outlined"
                                      :disabled="dialogDisabled || (editedItem.status !== 'STARTED') || !this.ownProfileComplete
                                      || !this.currentUserContracted(editedItem.group.id, editedItem.customer.id)
                                      || item.raw.savingAnswer || !item.raw.answer || (item.raw.answer.length === 0)"
                                      v-if="isCurrentUserParticipant(editedItem)"
                                      :multiple="false"
                                      persistent-hint>
                          <template v-slot:selection="{ fileNames }">
                            <template v-for="fileName in fileNames" :key="fileName">
                              <v-chip class="me-2" :color="getCustomerForegroundColor(editedItem.customer) || 'primary'" size="small" label>
                                {{ fileName }}
                              </v-chip>
                            </template>
                          </template>
                        </v-file-input>
                      </v-card>
                      <br>
                    </template>
                  </template>
                  <template v-slot:footer="{ page, itemsPerPage, pageCount, prevPage, nextPage, items }">
                    <div class="d-flex align-center justify-center pa-4">
                      <v-btn :disabled="page === 1" density="comfortable" icon="mdi-arrow-left"
                             variant="tonal" rounded @click="prevPage"></v-btn>
                      <div class="mx-2 text-caption" v-if="items.length > 1">
                        Questions {{ (page - 1) * itemsPerPage + 1 }} - {{ (page - 1) * itemsPerPage + items.length }} of {{ editedItem.questions.length }}
                      </div>
                      <div class="mx-2 text-caption" v-else-if="items.length > 0">
                        Question {{ (page - 1) * itemsPerPage + 1 }} of {{ editedItem.questions.length }}
                      </div>
                      <div class="mx-2 text-caption" v-else>
                        No questions
                      </div>
                      <v-btn :disabled="page >= pageCount" density="comfortable" icon="mdi-arrow-right"
                             variant="tonal" rounded @click="nextPage"></v-btn>
                    </div>
                  </template>
                </v-data-iterator>
                <v-row>
                  <v-col cols="12">
                    <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-btn variant="text"
                     v-if="this.initialized && hasAccessRight('virtual-panel-admin')"
                     @click="newFromTemplate(editedItem)" :disabled="dialogDisabled">Clone</v-btn>
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeView" :disabled="dialogDisabled">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogViewParticipant" max-width="50%">
          <v-card>
            <v-card-title>
              <v-row no-gutters>
                <v-col>Participant</v-col>
                <v-col class="text-right">
                  <v-icon size="small" color="success" v-tooltip="'Profile complete and participant contracted'"
                          v-if="profileComplete(editedParticipant) && participantContracted(editedParticipant.id,
                            editedParticipant.contract?.groupId, editedParticipant.contract?.customerId)">
                    mdi-check-circle
                  </v-icon>
                  <v-icon class="error" size="small" v-tooltip="'Profile not complete and participant not contracted'"
                          v-else-if="!profileComplete(editedParticipant) && !participantContracted(editedParticipant.id,
                            editedParticipant.contract?.groupId, editedParticipant.contract?.customerId)">
                    mdi-alert-circle
                  </v-icon>
                  <v-icon class="error" size="small" v-tooltip="'Participant not contracted'"
                          v-else-if="!participantContracted(editedParticipant.id, editedParticipant.contract?.groupId,
                            editedParticipant.contract?.customerId)">
                    mdi-alert-circle
                  </v-icon>
                  <v-icon class="error" size="small" v-tooltip="'Profile not complete'" v-else>
                    mdi-alert-circle
                  </v-icon>
                </v-col>
              </v-row>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-text-field label="Email" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      <template v-slot:append>
                        <v-icon size="small" color="success" v-tooltip="'Email address verified'" v-if="editedParticipant.emailVerified">
                          mdi-check-circle
                        </v-icon>
                        <v-icon class="error" size="small" v-tooltip="'Email address not verified'" v-else-if="editedParticipant.emailVerified === false">
                          mdi-alert-circle
                        </v-icon>
                      </template>
                      {{ editedParticipant.email }}
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-text-field label="First name" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ editedParticipant.firstName }}
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-text-field label="Last name" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ editedParticipant.lastName }}
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-text-field label="Job title" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ editedParticipant.jobTitle }}
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-text-field label="Affiliation" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ editedParticipant.affiliation }}
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-text-field label="Phone" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ editedParticipant.phone }}
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-text-field label="Country code" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ editedParticipant.countryCode }}
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="6">
                    <v-text-field label="CV available" :readonly="true" :focused="true" variant="plain"
                                  @update:focused="() => {}">
                      {{ editedParticipant.cvFileAvailable ? 'Yes' : 'No' }}
                    </v-text-field>
                  </v-col>
                  <v-col cols="6" class="text-right">
                    <v-btn v-if="hasAccessRight('virtual-panel-admin')"
                           variant="plain"
                           @click="this.$refs.uploadCvInput.click()">Upload CV</v-btn>
                    <v-btn variant="plain"
                            @click="downloadParticipantCv(editedParticipant.id)"
                           :disabled="!editedParticipant.cvFileAvailable">Download CV</v-btn>
                    <input ref="uploadCvInput"
                           class="d-none"
                           type="file"
                           accept=".doc,application/msword,
                                   .docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                   .pdf,application/pdf"
                           @change="e => uploadParticipantCv(editedParticipant, e.target.files[0])">
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-card-text class="error" v-if="editedParticipant.failedCvSave">
                      {{ editedParticipant.failedCvSave }}
                    </v-card-text>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-expansion-panels :multiple="true" :flat="true">
                    <v-expansion-panel v-if="editedParticipant.contract && hasAccessRight('virtual-panel-admin')">
                      <v-expansion-panel-title>
                        Contract details
                        <v-icon class="ms-2" size="small"
                                v-if="participantContracted(editedParticipant.id, editedParticipant.contract.groupId,
                                  editedParticipant.contract.customerId)">
                          mdi-calendar
                        </v-icon>
                        <v-icon class="ms-2 error" size="small" v-tooltip="'Not contracted'" v-else>
                          mdi-alert-circle
                        </v-icon>
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <v-card class="mx-auto overflow-x-scroll" max-width="328" elevation="0">
                          <v-card-text v-if="participantContracted(editedParticipant.id, editedParticipant.contract.groupId,
                            editedParticipant.contract.customerId)">
                            {{ 'Contracted with ' + editedParticipant.contract.customerName + ' (via ' + editedParticipant.contract.groupName + ') until end date' }}
                          </v-card-text>
                          <v-card-text v-else>
                            {{ 'Not contracted with ' + editedParticipant.contract.customerName + ' (via ' + editedParticipant.contract.groupName + ')' }}
                          </v-card-text>
                          <v-card-text class="error" v-if="editedParticipant.contract.failedSaveContract">
                            {{ editedParticipant.contract.failedSaveContract }}
                          </v-card-text>
                          <v-confirm-edit v-model="editedParticipant.contract.newContractEndDate"
                                          @save="saveContract(editedParticipant)">
                            <template v-slot:default="{ model: proxyModel, actions }">
                              <v-date-picker v-model="proxyModel.value" title="Contract end date (exclusive)"
                                             :allowed-dates="(date) => date.localWithoutTime() > new Date().localWithoutTime()">
                                <template v-slot:actions>
                                  <v-icon class="ms-2" size="small" @click="proxyModel.value=undefined"
                                          :disabled="proxyModel.value === undefined">mdi-delete</v-icon>
                                  <v-spacer></v-spacer>
                                  <component :is="actions"></component>
                                </template>
                              </v-date-picker>
                            </template>
                          </v-confirm-edit>
                        </v-card>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeViewParticipant" :disabled="dialogDisabled">Close</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogQuestion" max-width="40%">
          <v-card>
            <v-form lazy-validation @submit.prevent="validateAndSaveQuestion" ref="virtual_panel_question_form" :disabled="dialogDisabled">
              <v-card-title>
                <span class="text-h5">{{ questionFormTitle }}</span>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-textarea v-model="editedQuestion.question" :rules="rules.question"
                                  label="Question text" rows="2" variant="filled" auto-grow shaped></v-textarea>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-range-slider v-model="editedQuestion.answer_length" :rules="rules.answer_length"
                                      color="primary"
                                      label="Permitted answer length"
                                      hint="Set the minimum and maximum length of the answer in characters."
                                      :persistent-hint="true"
                                      step="1" thumb-label="always" min="0" max="100000">
                      </v-range-slider>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn variant="text" @click="closeQuestion(true)" :disabled="dialogDisabled">Cancel</v-btn>
                <v-btn type="submit" variant="text" :disabled="dialogDisabled">Save</v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogStartPanel" max-width="25%">
          <v-card>
            <v-card-title>Confirm start panel</v-card-title>
            <v-card-text class="error" v-if="failedStart">Failed to start virtual expert panel!</v-card-text>
            <v-card-text>Are you sure that you want to start this virtual expert panel? This will result in invitations
              to participate in the panel being emailed to all the participants. They will be able to submit answers to
              the questions.</v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-card-actions class="mt-10">
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeStartPanel" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="startPanelConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogResumePanel" max-width="25%">
          <v-card>
            <v-card-title>Confirm resume panel</v-card-title>
            <v-card-text class="error" v-if="failedResume">Failed to resume virtual expert panel!</v-card-text>
            <v-card-text>Are you sure that you want to resume this virtual expert panel? This will result in all the
              participants receiving an email informing them that the panel has been resumed. They will be able to
              start or continue submitting answers to the questions.</v-card-text>
            <v-card-text>
              <v-row>
                <v-col v-if="editedItem.total_running_time_in_seconds_dynamic >= (editedItem.duration_in_hours * 3600)">
                  The panel has been running for {{ this.formatSecondsDuration(editedItem.total_running_time_in_seconds_dynamic) }}
                  and the total duration is set to {{ this.formatDuration(editedItem.duration_in_hours) }}.
                  There is no duration left for this panel. You need to extend its duration in order to resume the panel.
                </v-col>
                <v-col v-else>
                  The panel has been running for {{ this.formatSecondsDuration(editedItem.total_running_time_in_seconds_dynamic) }}
                  and the total duration is set to {{ this.formatDuration(editedItem.duration_in_hours) }}.
                  If you like, you can extend the total duration of the panel by entering a new duration in hours.
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-number-input v-model="editedItem.new_duration_in_hours"
                                  :reverse="false"
                                  controlVariant="default"
                                  label="Duration in hours"
                                  :min="(editedItem.total_running_time_in_seconds_dynamic >= (editedItem.duration_in_hours * 3600)) ?
                                  Math.ceil(editedItem.total_running_time_in_seconds_dynamic / 3600) :
                                  editedItem.duration_in_hours"
                                  :hideInput="false"
                                  :inset="false"></v-number-input>
                </v-col>
              </v-row>
            </v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-card-actions class="mt-10">
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeResumePanel" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="resumePanelConfirm(
                  (editedItem.new_duration_in_hours !== editedItem.duration_in_hours) ?
                  editedItem.new_duration_in_hours : undefined)" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogCompletePanel" max-width="25%">
          <v-card>
            <v-card-title>Confirm complete panel</v-card-title>
            <v-card-text class="error" v-if="failedComplete">Failed to complete virtual expert panel!</v-card-text>
            <v-card-text>Are you sure that you want to complete this virtual expert panel before it is scheduled to
              end? This is not recommended as invitations to participate in the panel have been emailed to all the
              participants. They have been able to submit answers to the questions and will now be notified that the
              panel has completed.
            </v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-card-actions class="mt-10">
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeCompletePanel" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="completePanelConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDelete" max-width="25%">
          <v-card>
            <v-card-title>Confirm delete</v-card-title>
            <v-card-text class="error" v-if="failedDelete">Failed to delete virtual expert panel!</v-card-text>
            <v-card-text>Are you sure that you want to delete this virtual expert panel?</v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-card-actions class="mt-10">
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeDelete" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="deleteItemConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogRestore" max-width="25%">
          <v-card>
            <v-card-title>Confirm restore</v-card-title>
            <v-card-text class="error" v-if="failedRestore">Failed to restore virtual expert panel!</v-card-text>
            <v-card-text>Are you sure that you want to restore this virtual expert panel?</v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-card-actions class="mt-10">
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeRestore" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="restoreItemConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogShareWithCustomer" max-width="25%">
          <v-card>
            <v-card-title>Confirm sharing</v-card-title>
            <v-card-text class="error" v-if="failedSharing">Failed to share virtual expert panel!</v-card-text>
            <v-card-text class="mb-10">Are you sure that you want to share this virtual expert panel with the customer?</v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-divider :vertical="true"></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeShareWithCustomer" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="shareWithCustomerItemConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogUnshareWithCustomer" max-width="25%">
          <v-card>
            <v-card-title>Confirm remove sharing</v-card-title>
            <v-card-text class="error" v-if="failedRemoveSharing">Failed to remove sharing of virtual expert panel!</v-card-text>
            <v-card-text class="mb-10">Are you sure that you want to remove sharing of this virtual expert panel with the customer?</v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-divider :vertical="true"></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeUnshareWithCustomer" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="unshareWithCustomerItemConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDownload" max-width="25%">
          <v-card>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogProfile" max-width="50%">
          <template v-slot:activator="{ props }">
            <v-btn color="primary" dark class="mb-2" v-bind="props"
                   v-if="hasAccessRight('virtual-panel') && !this.ownProfileComplete">Complete profile</v-btn>
          </template>
          <v-card>
            <ProfileForm
                sub-title="In order to participate as an expert in a virtual panel, you need to provide these additional details."
                cv-sub-title="You must also upload your curriculum vitae."
                :display-cv="true"
                :all-fields-required="true"
                v-on:closed="closeProfile()">
            </ProfileForm>
          </v-card>
        </v-dialog>
        <v-btn variant="text" icon="mdi-filter" @click.stop="filter_drawer = !filter_drawer"></v-btn>
      </v-toolbar>
    </template>
    <template v-slot:[`item.title`]="{ item }">
      <div :style="renderDisabledStyle(item)">{{ item.title }}</div>
    </template>
    <template v-slot:[`item.customer`]="{ item }">
      <v-chip-group :column="true">
        <v-chip class="me-2" size="small" :label="true" :style="renderDisabledStyle(item)"
                v-if="item.customer?.name">
          {{ item.customer.name }}
        </v-chip>
        <v-icon class="me-2" size="small" v-tooltip="'Shared with customer'" v-if="item.shared_with_customer">mdi-account-outline</v-icon>
      </v-chip-group>
    </template>
    <template v-slot:[`item.created_date_time`]="{ item }">
      <div :style="renderDisabledStyle(item)">{{ formatTimestamp(item.created_date_time) }}</div>
    </template>
    <template v-slot:[`item.started_date_time`]="{ item }">
      <div :style="renderDisabledStyle(item)">{{ formatTimestamp(item.started_date_time) }}</div>
    </template>
    <template v-slot:[`item.completed_date_time`]="{ item }">
      <div :style="renderDisabledStyle(item)">{{ formatTimestamp(item.completed_date_time) }}</div>
    </template>
    <template v-slot:[`item.participants`]="{ item }">
      <div v-for="participant in item.participants" :key="participant">
        <v-chip class="me-2" size="small" :label="true"
                :style="renderDisabledStyle(item)"
                @click.stop="viewParticipant(participant, item.group, item.customer)">
          <template v-slot:prepend>
            <v-icon class="me-2 error" size="small" v-tooltip="'Profile not complete and participant not contracted'"
                    v-if="!participantContracted(participant.id, item.group.id, item.customer.id) && !profileComplete(participant)">
              mdi-alert-circle
            </v-icon>
            <v-icon class="me-2 error" size="small" v-tooltip="'Participant not contracted'"
                    v-else-if="!participantContracted(participant.id, item.group.id, item.customer.id)">
              mdi-alert-circle
            </v-icon>
            <v-icon class="me-2 error" size="small" v-tooltip="'Profile not complete'"
                    v-else-if="!profileComplete(participant)">
              mdi-alert-circle
            </v-icon>
          </template>
          <template v-slot:append>
            <v-icon class="ms-2" size="small" color="success" v-tooltip="'Panel completed'"
                    v-if="item.completed_answers && (item.completed_answers[participant.id]?.length === item.questions?.length)">
              mdi-check-circle
            </v-icon>
            <v-icon class="ms-2" size="small" v-tooltip="'Started providing answers'"
                    v-else-if="(item.completed_answers && (item.completed_answers[participant.id]?.length > 0))
                    || (item.partial_answers && (item.partial_answers[participant.id]?.length > 0))">
              mdi-comment-text-outline
            </v-icon>
          </template>
          {{ formatParticipant(participant) }}
          <div class="ml-2">
            [{{ (item.completed_answers && item.completed_answers[participant.id]?.length) || 0 }} answered
            {{ (item.partial_answers && item.partial_answers[participant.id]?.length
              && (', ' + item.partial_answers[participant.id]?.length + ' partial')) || '' }}]
          </div>
        </v-chip>
        <br/>
      </div>
    </template>
    <template v-slot:[`item.duration`]="{ item }">
      <div :style="renderDisabledStyle(item)">
        {{ formatDuration(item.duration_in_hours) }}
      </div>
    </template>
    <template v-slot:[`item.total_running_time_in_seconds`]="{ item }">
      <div :style="renderDisabledStyle(item)">
        {{ this.formatTotalRunningTime(item.total_running_time_in_seconds_dynamic) }}
      </div>
    </template>
    <template v-slot:[`item.status`]="{ item }">
      <v-chip-group :column="true">
        <v-chip class="me-2" size="small" :label="true" :style="renderDisabledStyle(item)">
          <template v-slot:prepend>
            <v-icon class="me-2"
                    :icon="translateStatusToIcon(item.status)"
                    :color="translateStatusToColor(item.status)"></v-icon>
          </template>
          {{ formatStatus(item.status) }}
        </v-chip>
      </v-chip-group>
    </template>
    <template v-slot:[`item.time_left`]="{ item }">
      <div v-html="renderTimeLeft(item.time_left_in_seconds)" v-if="(item.status === 'STARTED')"></div>
    </template>
    <template v-slot:[`item.questions`]="{ item }">
      <v-chip class="me-2" size="small" :label="true" :style="renderDisabledStyle(item)"
              v-if="item.questions.length > 1">
        {{ item.questions.length }} questions
      </v-chip>
      <v-chip class="me-2" size="small" :label="true" :style="renderDisabledStyle(item)"
              v-else-if="item.questions.length > 0">
        One question
      </v-chip>
    </template>
    <template v-slot:[`item.actions`]="{ item }" v-if="hasAccessRight('virtual-panel-admin')">
      <v-icon size="small" class="me-2"
              v-tooltip="'Edit panel'"
              @click.stop="editItem(item)" v-if="item.editable">mdi-pencil</v-icon>
      <v-icon size="small" class="me-2"
              v-tooltip="'Start panel'"
              @click.stop="startPanel(item)"
              v-if="item.editable && (item.participants.length > 0) && (item.questions.length > 0)">
        mdi-play-circle-outline
      </v-icon>
      <v-icon size="small" class="me-2"
              v-tooltip="'Resume panel'"
              @click.stop="resumePanel(item)"
              v-if="(item.status === 'COMPLETED')">
        mdi-replay
      </v-icon>
      <v-icon size="small" class="me-2"
              v-tooltip="'Complete panel'"
              @click.stop="completePanel(item)"
              v-if="(item.status === 'STARTED')">
        mdi-stop-circle-outline
      </v-icon>
      <v-icon size="small" class="me-2"
              v-tooltip="'Download report'"
              @click.stop="downloadPanelReport(item)" v-if="(item.status !== 'CREATED')">
        mdi-file-word-box
      </v-icon>
      <v-icon size="small" class="me-2"
              v-tooltip="'Share with customer'"
              @click.stop="shareWithCustomer(item)" v-if="!item.shared_with_customer &&
        item.shareable && (item.group?.id !== item.customer?.id)">
        mdi-account-plus
      </v-icon>
      <v-icon size="small" class="me-2"
              v-tooltip="'Stop sharing with customer'"
              @click.stop="unshareWithCustomer(item)" v-if="item.shared_with_customer &&
        item.shareable">mdi-account-minus</v-icon>
      <v-icon size="small"
              v-tooltip="'Delete panel'"
              @click.stop="deleteItem(item)" v-if="item.editable || (item.status === 'COMPLETED')">mdi-delete</v-icon>
      <v-icon size="small"
              v-tooltip="'Restore panel'"
              @click.stop="restoreItem(item)" v-if="item.status === 'DELETED'">mdi-delete-sweep</v-icon>
    </template>
    <template v-slot:[`item.data-table-expand`]="{ item, internalItem, toggleExpand, isExpanded }">
      <td>
        <div v-if="item?.questions?.length > 0">
          <v-btn variant="text" density="comfortable"
                 @click.stop="toggleExpand(internalItem)"
                 :icon="true">
            <v-icon v-tooltip="'Expand panel'" v-if="!isExpanded(internalItem)">mdi-chevron-down</v-icon>
            <v-icon v-tooltip="'Collapse panel'" v-else>mdi-chevron-up</v-icon>
          </v-btn>
        </div>
      </td>
    </template>
    <template v-slot:expanded-row="{ columns, item }">
      <tr>
        <td :colspan="columns.length">
          <div v-for="(question, i) in item.questions" :key="i">
            <v-card title=""
                    :subtitle="`Question ${i + 1}`"
                    color="primary" variant="plain" elevation="0">
              <v-card-text>
                <v-row>
                  <v-col cols="6">
                    <div v-for="(questionLine, i) in question.question?.split('\n')" :key="i">
                      <p v-if="questionLine">{{questionLine}}</p>
                      <br v-else>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <template v-for="(participantAndHasAnswerFile, j) in getAnsweringParticipants(question.id, item.completed_answers, item.participants)" :key="j">
                      <v-chip class="me-2" size="small" :label="true"
                              @click.stop="viewParticipant(participantAndHasAnswerFile.participant, item.group, item.customer)">
                        <template v-slot:prepend>
                          <v-icon class="me-2" size="small" color="success" v-tooltip="'Complete answer'">mdi-check-circle</v-icon>
                        </template>
                        <template v-slot:append v-if="participantAndHasAnswerFile.hasAnswerFile">
                          <v-icon class="ms-2" size="small"
                                  v-tooltip="'Download attached document'"
                                  @click.stop="downloadPanelAsset(question.id, participantAndHasAnswerFile.participant.id)">
                            mdi-file-document
                          </v-icon>
                        </template>
                        {{ formatParticipant(participantAndHasAnswerFile.participant) }}
                      </v-chip>
                      <br/>
                    </template>
                    <template v-for="(participantAndHasAnswerFile, j) in getAnsweringParticipants(question.id, item.partial_answers, item.participants)" :key="j">
                      <v-chip class="me-2" size="small" :label="true"
                              @click.stop="viewParticipant(participantAndHasAnswerFile.participant, item.group, item.customer)">
                        <template v-slot:prepend>
                          <v-icon class="me-2" size="small" v-tooltip="'Partial answer'">mdi-comment-text-outline</v-icon>
                        </template>
                        <template v-slot:append v-if="participantAndHasAnswerFile.hasAnswerFile">
                          <v-icon class="ms-2" size="small"
                                  v-tooltip="'Download attached document'"
                                  @click.stop="downloadPanelAsset(question.id, participantAndHasAnswerFile.participant.id)">
                            mdi-file-document
                          </v-icon>
                        </template>
                        {{ formatParticipant(participantAndHasAnswerFile.participant) }}
                      </v-chip>
                      <br/>
                    </template>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </td>
      </tr>
    </template>
    <template v-slot:no-data>
      <v-progress-circular color="primary" v-if="!this.initialized" indeterminate></v-progress-circular>
      <v-card-text v-else>No data available</v-card-text>
    </template>
  </v-data-table>
  <v-navigation-drawer v-model="filter_drawer" location="right" v-bind:width="500">
    <v-card class="mx-auto epix-filter" elevation="0" ref="filters">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="filter_form">
              <v-row v-if="selectCustomerItems.length > 0">
                <v-col cols="9">
                  <v-select v-model="selectedCustomers" :items="selectCustomerItems"
                            :item-title="(item) => item.name"
                            :item-value="(item) => item"
                            label="Customers" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                            multiple></v-select>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneCustomers">All/None</v-btn>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="9">
                  <v-select v-model="selectedStatuses" :items="statusItems"
                            v-if="statusItems.length > 0"
                            :item-title="(item) => item.title"
                            :item-value="(item) => item"
                            label="Status" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                            multiple></v-select>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneStatuses">All/None</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import {defineComponent, ref} from "vue";
import axios from "axios";
import ProgressBar from 'primevue/progressbar';
import store from '@/store';
import ProfileForm from '@/components/ProfileForm.vue';

function addItemsIfNotExists(array, items) {
    const set = new Set(array);
    if (!Array.isArray(items)) {
        items = [items];
    }
    items.forEach(item => {
        if (!set.has(item)) {
            array.push(item);
        }
    });
    array.sort()
}

function formatStatus(status) {
  if (status === 'CREATED') {
    return 'Created'
  }
  if (status === 'STARTED') {
    return 'Started'
  }
  if (status === 'COMPLETED') {
    return 'Completed'
  }
  if (status === 'DELETED') {
    return 'Deleted'
  }
}

function debounce(func, timer, timeout = 1000) {
  return (...args) => {
    if (timer) {
      clearTimeout(timer);
    }
    return setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

const STATUS_CREATED = {
  title: formatStatus('CREATED'),
  value: 'CREATED'
}

const STATUS_STARTED = {
  title: formatStatus('STARTED'),
  value: 'STARTED'
}

const STATUS_COMPLETED = {
  title: formatStatus('COMPLETED'),
  value: 'COMPLETED'
}

const STATUS_DELETED = {
  title: formatStatus('DELETED'),
  value: 'DELETED'
}

const ALL_STATUSES = [STATUS_CREATED, STATUS_STARTED, STATUS_COMPLETED, STATUS_DELETED]
const ALL_ACTIVE_STATUSES = [STATUS_CREATED, STATUS_STARTED, STATUS_COMPLETED]

Date.prototype.localWithoutTime = function () {
  return new Date(this.getFullYear(), this.getMonth(), this.getDate());
}

export default defineComponent({
  name: 'VirtualPanelsPage',
  components: {
    ProgressBar,
    ProfileForm
  },
  data: () => ({
    initialized: false,
    filter_drawer: false,
    cloning: false,
    dialog: false,
    dialogDisabled: false,
    dialogProfile: false,
    failedSave: undefined,
    failedClone: undefined,
    dialogQuestion: false,
    dialogQuestionCallback: () => {},
    dialogView: false,
    dialogViewParticipant: false,
    dialogStartPanel: false,
    failedStart: false,
    dialogResumePanel: false,
    failedResume: false,
    dialogCompletePanel: false,
    failedComplete: false,
    dialogDelete: false,
    failedDelete: false,
    dialogRestore: false,
    failedRestore: false,
    dialogShareWithCustomer: false,
    failedSharing: false,
    dialogUnshareWithCustomer: false,
    dialogDownload: false,
    failedRemoveSharing: false,
    expanded: [],
    sortBy: [{ key: 'title', order: 'asc' }],
    headers: [
      {
        title: 'Virtual expert panel name',
        align: 'start',
        sortable: true,
        key: 'title',
      },
      { title: 'Customer', key: 'customer' },
      { title: 'Created at', key: 'created_date_time' },
      { title: 'Started or resumed at', key: 'started_date_time' },
      { title: 'Completed at', key: 'completed_date_time' },
      { title: 'Participants', key: 'participants' },
      { title: 'Duration', key: 'duration' },
      { title: 'Total running time', key: 'total_running_time_in_seconds' },
      { title: 'Status', key: 'status' },
      { title: 'Time left', key: 'time_left' },
      { title: 'Questions', key: 'questions' },
      { title: '', key: 'data-table-expand' },
      { title: 'Actions', key: 'actions', sortable: false },
    ],
    virtualPanels: [],
    filteredVirtualPanels: [],
    filteredStartedVirtualPanelIndices: [],
    editedIndex: -1,
    editedQuestionIndex: -1,
    virtualPanelContracts: [],
    customerItems: [],
    selectCustomerItems: [],
    selectedCustomers: [],
    statusItems: [...ALL_STATUSES],
    selectedStatuses: [...ALL_ACTIVE_STATUSES],
    groupItems: store.getters.stateUser.groups_by_names,
    participantItems: [],
    editedItem: {},
    editedParticipant: {},
    editedQuestion: {},
    participantSearch: null,
    participantMenu: false,
    failedAddNewParticipant: false,
    timeLeftTimer: undefined,
    completedAnswersTimer: undefined,
    partialAnswersTimer: undefined,
    contractsTimer: undefined,
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      },
      group: [
        value => {
          if (value) return true
          return 'You must enter an owner company.'
        }
      ],
      customer: [
        value => {
          if (value) return true
          return 'You must enter a customer.'
        }
      ],
      participants: [
        value => {
          if (value && (value.length > 0)) return true
          return 'You must enter at least one participant.'
        }
      ],
      title: [
        value => {
          if (value) return true
          return 'You must enter a virtual expert panel name.'
        }
      ],
      duration_in_hours: [
        value => {
          if (value && Number.isInteger(value) && (value > 0)) return true
          return 'You must enter a positive duration in whole hours.'
        }
      ],
      question: [
        value => {
          if (value) return true
          return 'You must enter a question.'
        }
      ],
      answer: [
        value => {
          if (value) return true
          return 'You must provide an answer to complete the panel.'
        }
      ],
      answer_length: [
        value => {
          if (Number.isInteger(value[0]) && Number.isInteger(value[1]) && (value[0] >= 0) && (value[0] <= value[1])) return true
          return 'You must enter a non-negative minimum answer length which is less than or equal to the maximum answer length.'
        }
      ]
    }
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'New Virtual Expert Panel' : 'Edit Virtual Expert Panel'
    },
    questionFormTitle() {
      return this.editedQuestionIndex === -1 ? 'New Question' : 'Edit Question'
    },
    defaultItem() {
      return {
        group: (this.groupItems.length === 1) ? this.groupItems[0] : '',
        id: undefined,
        title: undefined,
        customer: undefined,
        shared_with_customer: false,
        created_date_time: undefined,
        started_date_time: undefined,
        completed_date_time: undefined,
        duration_in_hours: undefined,
        total_running_time_in_seconds: undefined,
        approval_code: undefined,
        details: undefined,
        files: [],
        participants: [],
        status: 'CREATED',
        editable: true,
        shareable: false,
        questionsPage: ref(1),
        questions: []
      }
    },
    defaultQuestion() {
      return {
        id: undefined,
        question: undefined,
        answer_length: [100, 100000]
      }
    },
    ownProfileComplete() {
      return this.$store.getters.stateUser.email?.trim().length > 0 &&
          this.$store.getters.stateUser.email_verified &&
          this.$store.getters.stateUser.first_name?.trim().length > 0 &&
          this.$store.getters.stateUser.last_name?.trim().length > 0 &&
          this.$store.getters.stateUser.job_title?.trim().length > 0 &&
          this.$store.getters.stateUser.affiliation?.trim().length > 0 &&
          this.$store.getters.stateUser.phone?.trim().length > 0 &&
          this.$store.getters.stateUser.country_code?.trim().length === 2 &&
          this.$store.getters.stateUser.cv_file_available
    }
  },

  watch: {
    dialog(val) {
      val || this.close()
    },
    dialogProfile(val) {
      val || this.closeProfile()
    },
    dialogQuestion(val) {
      val || this.closeQuestion()
    },
    dialogView(val) {
      val || this.cloning || this.closeView()
    },
    dialogViewParticipant(val) {
      val || this.closeViewParticipant()
    },
    dialogStartPanel(val) {
      val || this.closeStartPanel()
    },
    dialogResumePanel(val) {
      val || this.closeResumePanel()
    },
    dialogCompletePanel(val) {
      val || this.closeCompletePanel()
    },
    dialogDelete(val) {
      val || this.closeDelete()
    },
    dialogRestore(val) {
      val || this.closeRestore()
    },
    dialogShareWithCustomer(val) {
      val || this.closeShareWithCustomer()
    },
    dialogUnshareWithCustomer(val) {
      val || this.closeUnshareWithCustomer()
    },
    virtualPanels() {
      this.filterVirtualPanels()
    },
    selectedCustomers() {
      this.filterVirtualPanels()
    },
    selectedStatuses() {
      this.filterVirtualPanels()
    }
  },

  created() {
    this.initialize()
    if (this.timeLeftTimer) {
      clearInterval(this.timeLeftTimer);
    }
    this.timeLeftTimer = setInterval(() => {
      this.populateVirtualPanelTimeLeftAndTotalRunningTimeDynamic(this.filteredStartedVirtualPanelIndices, this.filteredVirtualPanels);
    }, 1000);

    if (this.contractsTimer) {
      clearInterval(this.contractsTimer);
    }
    this.contractsTimer = setInterval(() => {
      this.loadContracts();
    }, 10000);

    if (this.completedAnswersTimer) {
      clearInterval(this.completedAnswersTimer);
    }
    this.completedAnswersTimer = setInterval(() => {
      this.populateVirtualPanelCompletedAnswers(this.filteredStartedVirtualPanelIndices, this.filteredVirtualPanels);
    }, 10000);

    if (this.partialAnswersTimer) {
      clearInterval(this.partialAnswersTimer);
    }
    this.partialAnswersTimer = setInterval(() => {
      this.populateVirtualPanelPartialAnswers(this.filteredStartedVirtualPanelIndices, this.filteredVirtualPanels);
    }, 10000);

    this.dialogProfile = this.isCurrentUserParticipantInAnyStartedPanel() && !this.ownProfileComplete
  },

  methods: {
    hasAccessRight(accessRight) {
      return this.$store.getters.stateUser?.access_rights?.includes(accessRight);
    },

    async loadVirtualPanels() {
      let data = axios.get('/virtual-panels',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {}
          });
      return data.then((response) => {
        this.virtualPanels = response.data.panels
        this.virtualPanels.forEach(panel => {
          if (panel.files) {
            panel.files = panel.files.map(f => {
              return new File([], f.filename, {
                type: f.content_type,
              })
            })
          }
        })

        this.participantItems = response.data.participants
      });
    },

    async loadCustomers() {
      let data = axios.get('/customers',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {}
          });
      return data.then((response) => {
        this.customerItems = response.data
        this.selectCustomerItems = [...this.customerItems]
        this.selectedCustomers = [...this.selectCustomerItems]
      });
    },

    async loadContracts() {
      let data = axios.get('/virtual-panel-contracts',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
          });
      return data.then((response) => {
        this.virtualPanelContracts = response.data
      });
    },

    async getVirtualPanelAnswers(virtualPanelId) {
      return axios.get('/virtual-panel-answers',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              virtual_panel_id: virtualPanelId
            }
          });
    },

    async getVirtualPanelCompletedAnswers(virtualPanelIds) {
      const formData = new FormData();
      virtualPanelIds.forEach(virtualPanelId =>
        formData.append("virtual_panel_ids", virtualPanelId)
      );

      return axios.post('/virtual-panel-completed-answers',
          formData,
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
          });
    },

    async getVirtualPanelPartialAnswers(virtualPanelIds) {
      const formData = new FormData();
      virtualPanelIds.forEach(virtualPanelId =>
        formData.append("virtual_panel_ids", virtualPanelId)
      );

      return axios.post('/virtual-panel-partial-answers',
          formData,
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
          });
    },

    initialize() {
      Promise.all([
        this.loadVirtualPanels(),
        this.loadCustomers(),
        this.loadContracts()
      ]).then(() => {
        console.log('Virtual expert panels loaded')
        this.editedItem = structuredClone(this.defaultItem)
        this.editedQuestion = Object.assign({}, this.defaultQuestion)
        this.initialized = true
      })
    },

    async newFromTemplate(itemToClone) {
      if (itemToClone) {
        try {
          this.dialogDisabled = true
          this.failedClone = undefined
          this.cloning = true
          let editedItem = structuredClone(itemToClone)
          let panelFiles = await Promise.all(this.editedItem.files.map(f => {
            return this.downloadAndReturnPanelFile(editedItem.id, f.name, f.type).then(fileBinary => {
              return new File([fileBinary], f.name, {
                type: f.type,
              })
            })
          })).catch(err => {
            this.cloning = false
            this.failedClone = 'Failed to download files for cloning the virtual expert panel!'
            throw err
          })
          this.dialogView = false
          this.$nextTick().then(() => {
            this.editedItem = {
              group: editedItem.group,
              id: undefined,
              title: editedItem.title,
              customer: editedItem.customer,
              shared_with_customer: false,
              created_date_time: undefined,
              started_date_time: undefined,
              completed_date_time: undefined,
              duration_in_hours: editedItem.duration_in_hours,
              total_running_time_in_seconds: undefined,
              approval_code: editedItem.approval_code,
              details: editedItem.details,
              files: panelFiles,
              participants: [...editedItem.participants],
              status: 'CREATED',
              editable: true,
              shareable: false,
              questionsPage: ref(1),
              questions: editedItem.questions.map(q => ({
                question: q.question,
                minimum_answer_length: q.minimum_answer_length,
                maximum_answer_length: q.maximum_answer_length,
                ordering: q.ordering
              }))
            }
            this.editedIndex = -1
            this.failedSave = undefined
            this.failedClone = undefined
            this.dialog = true
          }).catch(err => {
            this.cloning = false
            console.log(err);
          })
        } finally {
          this.dialogDisabled = false
        }
      }
    },

    editItem(item) {
      this.editedIndex = this.virtualPanels.indexOf(item)
      this.editedItem = structuredClone(item)
      this.failedSave = undefined
      this.dialog = true
    },

    addQuestion(dialogQuestionCallback) {
      this.dialogQuestionCallback = dialogQuestionCallback
      this.dialogQuestion = true
    },

    editQuestion(question) {
      this.editedQuestionIndex = this.editedItem.questions.indexOf(question)
      this.editedQuestion = {
        id: question.id,
        question: question.question,
        answer_length: [question.minimum_answer_length, question.maximum_answer_length]
      }
      this.dialogQuestion = true
    },

    view(item) {
      this.editedIndex = this.virtualPanels.indexOf(item)
      this.editedItem = item
      this.failedClone = undefined
      this.dialogDisabled = true
      this.dialogView = true
      this.getVirtualPanelAnswers(item.id).then((response) => {
        response.data.forEach(answer => {
          const question = this.editedItem.questions.find(q => q.id === answer.question_id)
          if (question) {
            question.answer = answer.answer
            if (answer.answer_file) {
              question.answerFile = new File([], answer.answer_file.filename, {
                type: answer.answer_file.content_type,
              })
            }
            question.savingAnswer = false
            question.failedSaveAnswer = undefined
          }
        })
      }).catch(err => {
        console.log(err);
        this.closeView()
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    viewParticipant(participant, group, customer) {
      this.editedParticipant = participant
      this.editedParticipant.failedCvSave = undefined
      this.editedParticipant.contract = {
        groupId: group.id,
        groupName: group.name,
        customerId: customer.id,
        customerName: customer.name,
        failedSaveContract: undefined,
        newContractEndDate: this.getContractEndDate(participant.id, group.id, customer.id)
      }
      this.dialogViewParticipant = true
    },

    moveQuestionUp(questionIndex) {
      if (questionIndex > 0) {
        const question = this.editedItem.questions[questionIndex]
        this.editedItem.questions.splice(questionIndex, 1)
        this.editedItem.questions.splice(questionIndex - 1, 0, question)
      }
    },

    moveQuestionDown(questionIndex) {
      if (questionIndex < this.editedItem.questions.length - 1) {
        const question = this.editedItem.questions[questionIndex]
        this.editedItem.questions.splice(questionIndex, 1)
        this.editedItem.questions.splice(questionIndex + 1, 0, question)
      }
    },

    startPanel(item) {
      this.editedIndex = this.virtualPanels.indexOf(item)
      this.editedItem = structuredClone(item)
      this.failedStart = false
      this.dialogStartPanel = true
    },

    resumePanel(item) {
      this.editedIndex = this.virtualPanels.indexOf(item)
      this.editedItem = structuredClone(item)
      this.editedItem.new_duration_in_hours = this.editedItem.duration_in_hours
      this.failedResume = false
      this.dialogResumePanel = true
    },

    completePanel(item) {
      this.editedIndex = this.virtualPanels.indexOf(item)
      this.editedItem = structuredClone(item)
      this.failedComplete = false
      this.dialogCompletePanel = true
    },

    deleteItem(item) {
      this.editedIndex = this.virtualPanels.indexOf(item)
      this.editedItem = structuredClone(item)
      this.failedDelete = false
      this.dialogDelete = true
    },

    restoreItem(item) {
      this.editedIndex = this.virtualPanels.indexOf(item)
      this.editedItem = structuredClone(item)
      this.failedRestore = false
      this.dialogRestore = true
    },

    shareWithCustomer(item) {
      this.editedIndex = this.virtualPanels.indexOf(item)
      this.editedItem = structuredClone(item)
      this.failedSharing = false
      this.dialogShareWithCustomer = true
    },

    unshareWithCustomer(item) {
      this.editedIndex = this.virtualPanels.indexOf(item)
      this.editedItem = structuredClone(item)
      this.failedRemoveSharing = false
      this.dialogUnshareWithCustomer = true
    },

    async downloadAndReturnPanelFile(virtualPanelId, fileName, contentType) {
      const formData = new FormData();
      formData.append("virtual_panel_id", virtualPanelId)
      formData.append("file_name", fileName)
      formData.append("content_type", contentType)

      return axios.post('/virtual-panel-files',
          formData,
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            responseType: 'blob'
          })
          .then((response) => {
            return response.data;
          });
    },


    downloadPanelFile(virtualPanelId, fileName, contentType) {
      this.dialogDisabled = true

      this.downloadAndReturnPanelFile(virtualPanelId, fileName, contentType)
          .then(data => {
            const href = URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('target', '_blank');
            link.click();
            URL.revokeObjectURL(href);
          })
          .finally(() => {
            this.dialogDisabled = false
          });
    },

    downloadPanelReport(item) {
      this.dialogDownload = true
      this.dialogDisabled = true
      axios.get(`/virtual-panel/${item.id}`,
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            responseType: 'blob'
          })
          .then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('target', '_blank');
            link.click();
            URL.revokeObjectURL(href);
          })
          .finally(() => {
            this.dialogDisabled = false
            this.dialogDownload = false
          });
    },

    downloadPanelAsset(questionId, userId) {
      this.dialogDownload = true
      this.dialogDisabled = true
      axios.get(`/virtual-panel-answer-file/${questionId}/${userId}`,
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            responseType: 'blob'
          })
          .then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('target', '_blank');
            link.click();
            URL.revokeObjectURL(href);
          })
          .finally(() => {
            this.dialogDisabled = false
            this.dialogDownload = false
          });
    },

    async uploadParticipantCv(editedParticipant, cvFile) {
      this.dialogDisabled = true
      editedParticipant.failedCvSave = undefined
      const formData = new FormData();
      formData.append("user_id", editedParticipant.id);
      if (cvFile) {
        formData.append("cv_file", cvFile);
      }
      return axios.post('/virtual-panel-cvs', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      }).then((response) => {
        editedParticipant.cvFileAvailable = !!response.data;
      }).catch(err => {
        if (err.response.status === 413) {
          editedParticipant.failedCvSave = 'Failed to save CV! ' + err.response.data.detail
        } else {
          editedParticipant.failedCvSave = 'Failed to save CV! You can close and reopen this dialog to make another attempt.';
        }
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false;
      });
    },

    downloadParticipantCv(userId) {
      this.dialogDisabled = true
      axios.get(`/virtual-panel-cv-file/${userId}`,
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            responseType: 'blob'
          })
          .then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('target', '_blank');
            link.click();
            URL.revokeObjectURL(href);
          })
          .finally(() => {
            this.dialogDisabled = false
          });
    },

    startPanelConfirm() {
      this.dialogDisabled = true
      const formData = new FormData();
      formData.append("group", JSON.stringify(this.editedItem.group))
      formData.append("virtual_panel_id", this.editedItem.id)
      formData.append("existing_status", 'CREATED')
      formData.append("new_status", 'STARTED')
      let data = axios.put('/virtual-panels', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      });
      data.then((response) => {
        let result = response.data
        this.virtualPanels[this.editedIndex].status = result.status
        this.virtualPanels[this.editedIndex].duration_in_hours = result.duration_in_hours
        this.virtualPanels[this.editedIndex].started_date_time = result.started_date_time
        this.virtualPanels[this.editedIndex].completed_date_time = result.completed_date_time
        this.virtualPanels[this.editedIndex].total_running_time_in_seconds = result.total_running_time_in_seconds
        this.virtualPanels[this.editedIndex].editable = result.editable
        // Note: Need to call filterVirtualPanels() since the watch is not triggered by the update
        this.filterVirtualPanels()
        this.failedStart = false
        this.closeStartPanel()
      }).catch(err => {
        this.failedStart = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    resumePanelConfirm(newDurationInHours) {
      this.dialogDisabled = true
      const formData = new FormData();
      formData.append("group", JSON.stringify(this.editedItem.group))
      formData.append("virtual_panel_id", this.editedItem.id)
      formData.append("existing_status", 'COMPLETED')
      formData.append("new_status", 'STARTED')
      if (newDurationInHours) {
        formData.append("new_duration_in_hours", newDurationInHours)
      }
      let data = axios.put('/virtual-panels', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      });
      data.then((response) => {
        let result = response.data
        this.virtualPanels[this.editedIndex].status = result.status
        this.virtualPanels[this.editedIndex].duration_in_hours = result.duration_in_hours
        this.virtualPanels[this.editedIndex].started_date_time = result.started_date_time
        this.virtualPanels[this.editedIndex].completed_date_time = result.completed_date_time
        this.virtualPanels[this.editedIndex].total_running_time_in_seconds = result.total_running_time_in_seconds
        this.virtualPanels[this.editedIndex].editable = result.editable
        // Note: Need to call filterVirtualPanels() since the watch is not triggered by the update
        this.filterVirtualPanels()
        this.failedResume = false
        this.closeResumePanel()
      }).catch(err => {
        this.failedResume = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    completePanelConfirm() {
      this.dialogDisabled = true
      const formData = new FormData();
      formData.append("group", JSON.stringify(this.editedItem.group))
      formData.append("virtual_panel_id", this.editedItem.id)
      formData.append("existing_status", 'STARTED')
      formData.append("new_status", 'COMPLETED')
      let data = axios.put('/virtual-panels', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      });
      data.then((response) => {
        let result = response.data
        this.virtualPanels[this.editedIndex].status = result.status
        this.virtualPanels[this.editedIndex].duration_in_hours = result.duration_in_hours
        this.virtualPanels[this.editedIndex].started_date_time = result.started_date_time
        this.virtualPanels[this.editedIndex].completed_date_time = result.completed_date_time
        this.virtualPanels[this.editedIndex].total_running_time_in_seconds = result.total_running_time_in_seconds
        this.virtualPanels[this.editedIndex].editable = result.editable
        // Note: Need to call filterVirtualPanels() since the watch is not triggered by the update
        this.filterVirtualPanels()
        this.failedComplete = false
        this.closeCompletePanel()
      }).catch(err => {
        this.failedComplete = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    deleteItemConfirm() {
      this.dialogDisabled = true
      let data = axios.delete('/virtual-panels', {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
        params: {
          group_id: this.editedItem.group?.id,
          virtual_panel_id: this.editedItem.id
        }
      });
      data.then((response) => {
        let result = response.data
        this.virtualPanels[this.editedIndex].status = result.status
        this.virtualPanels[this.editedIndex].editable = result.editable
        // Note: Need to call filterVirtualPanels() since the watch is not triggered by splice
        this.filterVirtualPanels()
        this.failedDelete = false
        this.closeDelete()
      }).catch(err => {
        this.failedDelete = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    restoreItemConfirm() {
      this.dialogDisabled = true
      const formData = new FormData();
      formData.append("group", JSON.stringify(this.editedItem.group))
      formData.append("virtual_panel_id", this.editedItem.id)
      formData.append("existing_status", 'DELETED')
      formData.append("new_status", this.editedItem.completed_date_time ? 'COMPLETED' : 'CREATED')
      let data = axios.put('/virtual-panels', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      });
      data.then((response) => {
        let result = response.data
        this.virtualPanels[this.editedIndex].status = result.status
        this.virtualPanels[this.editedIndex].duration_in_hours = result.duration_in_hours
        this.virtualPanels[this.editedIndex].started_date_time = result.started_date_time
        this.virtualPanels[this.editedIndex].completed_date_time = result.completed_date_time
        this.virtualPanels[this.editedIndex].total_running_time_in_seconds = result.total_running_time_in_seconds
        this.virtualPanels[this.editedIndex].editable = result.editable
        // Note: Need to call filterVirtualPanels() since the watch is not triggered by the update
        this.filterVirtualPanels()
        this.failedRestore = false
        this.closeRestore()
      }).catch(err => {
        this.failedRestore = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    shareWithCustomerItemConfirm() {
      this.dialogDisabled = true
      this.editedItem.shared_with_customer = true
      this.save().then(() => {
        this.failedSharing = false
        this.closeShareWithCustomer()
      }).catch(err => {
        this.failedSharing = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    unshareWithCustomerItemConfirm() {
      this.dialogDisabled = true
      this.editedItem.shared_with_customer = false
      this.save().then(() => {
        this.failedRemoveSharing = false
        this.closeUnshareWithCustomer()
      }).catch(err => {
        this.failedRemoveSharing = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    rowProps() {
      return {
        class: "ciencia_row"
      };
    },

    handleRowClicked(event, row) {
      this.view(row.item)
    },

    close() {
      this.cloning = false
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = structuredClone(this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeProfile() {
      this.dialogProfile = false
      this.initialized = false
      this.virtualPanels = []
      this.loadVirtualPanels()
    },

    closeQuestion(cancel) {
      this.dialogQuestion = false
      this.$nextTick(() => {
        if (!cancel) {
          this.dialogQuestionCallback()
        }
        this.dialogQuestionCallback = () => {}
        this.editedQuestion = Object.assign({}, this.defaultQuestion)
        this.editedQuestionIndex = -1
      })
    },

    closeView() {
      this.cloning = false
      this.dialogView = false
      this.$nextTick(() => {
        this.editedItem = structuredClone(this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeViewParticipant() {
      this.dialogViewParticipant = false
      this.$nextTick(() => {
        this.editedParticipant = {}
      })
    },

    closeStartPanel() {
      this.dialogStartPanel = false
      this.$nextTick(() => {
        this.editedItem = structuredClone(this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeResumePanel() {
      this.dialogResumePanel = false
      this.$nextTick(() => {
        this.editedItem = structuredClone(this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeCompletePanel() {
      this.dialogCompletePanel = false
      this.$nextTick(() => {
        this.editedItem = structuredClone(this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeDelete() {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.editedItem = structuredClone(this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeRestore() {
      this.dialogRestore = false
      this.$nextTick(() => {
        this.editedItem = structuredClone(this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeShareWithCustomer() {
      this.dialogShareWithCustomer = false
      this.$nextTick(() => {
        this.editedItem = structuredClone(this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeUnshareWithCustomer() {
      this.dialogUnshareWithCustomer = false
      this.$nextTick(() => {
        this.editedItem = (this.defaultItem)
        this.editedIndex = -1
      })
    },

    computeQuestionIndex(page, itemsPerPage, pageIndex) {
      return ((page - 1) * itemsPerPage) + pageIndex;
    },

    validateAndSave() {
      this.dialogDisabled = true
      this.$refs.virtual_panel_form.validate().then(validationResult => {
        if (validationResult.valid) {
          return this.save().then(() => {
            this.failedSave = undefined
            this.close()
          }).catch(err => {
            if (err.response.status === 413) {
              this.failedSave = 'Failed to save virtual expert panel! ' + err.response.data.detail
            } else {
              this.failedSave = 'Failed to save virtual expert panel!'
            }
            console.log(err);
          });
        }
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    validateAndSaveQuestion() {
      this.$refs.virtual_panel_question_form.validate().then(validationResult => {
        if (validationResult.valid) {
          if (!this.editedItem.questions) {
            this.editedItem.questions = []
          }
          let question = {
            question: this.editedQuestion.question,
            minimum_answer_length: this.editedQuestion.answer_length[0],
            maximum_answer_length: this.editedQuestion.answer_length[1]
          }
          if (this.editedQuestionIndex > -1) {
            Object.assign(this.editedItem.questions[this.editedQuestionIndex], question)
          } else {
            this.editedItem.questions.push(question)
          }
          this.closeQuestion(false)
        }
      });
    },

    save() {
      if (!this.editedItem.participants) {
        this.editedItem.participants = []
      }
      if (!this.editedItem.questions) {
        this.editedItem.questions = []
      }
      if (!this.editedItem.files) {
        this.editedItem.files = []
      }

      let metaData = Object.assign({}, this.editedItem, {})
      delete metaData.id
      delete metaData.group
      delete metaData.files
      delete metaData.participants
      delete metaData.questionsPage
      delete metaData.questions
      delete metaData.shared_with_customer
      delete metaData.created_date_time
      delete metaData.started_date_time
      delete metaData.completed_date_time
      delete metaData.total_running_time_in_seconds
      delete metaData.status
      delete metaData.editable
      delete metaData.shareable

      const formData = new FormData();
      formData.append("group", JSON.stringify(this.editedItem.group))
      if (this.editedItem.id) {
        formData.append("virtual_panel_id", this.editedItem.id)
      }
      formData.append("metadata", JSON.stringify(metaData));
      this.editedItem.files.forEach(f => {
        formData.append("files", f);
      })
      this.editedItem.participants.forEach(p => {
        formData.append("participants", JSON.stringify(p));
      })
      this.editedItem.questions.forEach((p, index) => {
        formData.append("questions", JSON.stringify({...p, 'ordering': index}));
      })
      formData.append("shared_with_customer", this.editedItem.shared_with_customer)
      let data = axios.post('/virtual-panels', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      });
      return data.then((response) => {
        let result = response.data
        result.questionsPage = ref(1)

        if (response.data.files) {
          response.data.files = response.data.files.map(f => {
            return new File([], f.filename, {
              type: f.content_type,
            })
          })
        }

        if (this.editedIndex > -1) {
          Object.assign(this.virtualPanels[this.editedIndex], result)
        } else {
          this.virtualPanels.push(result)
          // Note: For some reason need to call filterVirtualPanels() since the watch is not triggered by push
          this.filterVirtualPanels()
        }
      });
    },

    addParticipant(email) {
      const formData = new FormData();
      formData.append("email", email)
      return axios.post('/virtual-panel-participants', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      });
    },

    saveAnswer(virtualPanelId, questionId, answer, answerFile) {
      const formData = new FormData();
      formData.append("virtual_panel_id", virtualPanelId)
      formData.append("question_id", questionId)
      formData.append("answer", answer)
      if (answerFile) {
        formData.append("answer_file", answerFile);
      }
      return axios.post('/virtual-panel-answers', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      });
    },

    async saveContract(participant) {
      let contract = participant.contract
      const formData = new FormData();
      formData.append("group_id", contract.groupId)
      formData.append("customer_id", contract.customerId)
      formData.append("user_id", participant.id)
      let newContractEndDateString = contract.newContractEndDate ? contract.newContractEndDate.toISOString() : undefined
      if (newContractEndDateString) {
        formData.append("end_date", newContractEndDateString);
      }

      this.dialogDisabled = true
      return axios.post('/virtual-panel-contracts', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      }).then((response) => {
        if (newContractEndDateString) {
          this.setContractEndDate(participant.id, contract.groupId, contract.customerId, response.data.contract_end_date)
        }
        else {
          this.setContractEndDate(participant.id, contract.groupId, contract.customerId, undefined)
        }
        contract.failedSaveContract = undefined
      }).catch(err => {
        contract.failedSaveContract = "Failed to save contract! Please close and reopen this dialog, to make another attempt."
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      })
    },

    toggleAllNoneCustomers() {
      if (this.selectedCustomers.length === this.selectCustomerItems.length) {
        this.selectedCustomers = [];
      } else {
        this.selectedCustomers = [...this.selectCustomerItems]
      }
    },

    toggleAllNoneStatuses() {
      if (this.selectedStatuses.length === this.statusItems.length) {
        this.selectedStatuses = [];
      } else {
        this.selectedStatuses = [...this.statusItems]
      }
    },

    filterVirtualPanels() {
      this.filteredVirtualPanels = this.virtualPanels.filter(item => {
        return this.selectedStatuses.some(s => item.status === s.value)
            && (!this.selectCustomerItems || (this.selectCustomerItems.length === 0)
                || this.selectedCustomers.some(c => (!item.customer && (c.id == null)) || item.customer.id === c.id))
      });
      let filteredVirtualPanelIndices = this.filteredVirtualPanels.map((item, index) => index);
      this.populateVirtualPanelTimeLeftAndTotalRunningTimeDynamic(filteredVirtualPanelIndices, this.filteredVirtualPanels);
      this.populateVirtualPanelCompletedAnswers(filteredVirtualPanelIndices, this.filteredVirtualPanels);
      this.populateVirtualPanelPartialAnswers(filteredVirtualPanelIndices, this.filteredVirtualPanels);
      this.filteredStartedVirtualPanelIndices = this.filteredVirtualPanels.map((item, index) => {
        if (item.status === 'STARTED') {
          return index;
        }
      }).filter(index => index !== undefined);

      this.dialogProfile = this.dialogProfile || (this.isCurrentUserParticipantInAnyStartedPanel() && !this.ownProfileComplete)
    },

    formatParticipant(participant) {
      if (participant.firstName && (participant.firstName.trim() !== '') && participant.lastName &&
          (participant.lastName.trim() !== '')) {
        return participant.firstName + ' ' + participant.lastName + ' (' + participant.email + ')'
      }

      if (participant.firstName && (participant.firstName.trim() !== '')) {
        return participant.firstName + ' (' + participant.email + ')'
      }

      if (participant.lastName && (participant.lastName.trim() !== '')) {
        return participant.lastName + ' (' + participant.email + ')'
      }

      if (participant.email) {
        return participant.email
      }

      return '[Unknown participant]'
    },

    formatDuration(durationInHours) {
      if (!durationInHours) {
        return '';
      }

      if (durationInHours === 1) {
        return durationInHours + ' hour';
      }

      return durationInHours + ' hours';
    },

    formatSecondsDuration(durationInSeconds) {
      let durationInWholeSeconds = Math.ceil(durationInSeconds);
      let hours   = Math.floor(durationInWholeSeconds / 3600);
      let minutes = Math.floor((durationInWholeSeconds - (hours * 3600)) / 60);
      let seconds = Math.floor(durationInWholeSeconds - (hours * 3600) - (minutes * 60));
      let hoursString = hours + (hours === 1 ? ' hour' : ' hours');
      let minutesString = minutes + (minutes === 1 ? ' minute' : ' minutes');
      let secondsString = seconds + (seconds === 1 ? ' second' : ' seconds');
      if (hours > 0) {
        return hoursString + ', ' + minutesString + ', ' + secondsString;
      }
      if (minutes > 0) {
        return minutesString + ', ' + secondsString;
      }
      return secondsString;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) {
        return '';
      }
      return new Date(timestamp).toLocaleString();
    },

    formatStatus(status) {
      return formatStatus(status)
    },

    translateStatusToIcon(status) {
      if (status === 'CREATED') {
        return 'mdi-pencil-outline'
      }
      if (status === 'STARTED') {
        return 'mdi-progress-clock'
      }
      if (status === 'COMPLETED') {
        return 'mdi-checkbox-marked-circle'
      }
      if (status === 'DELETED') {
        return 'mdi-delete'
      }
    },

    translateStatusToColor(status) {
      if (status === 'COMPLETED') {
        return 'success'
      }
      if (status === 'DELETED') {
        return 'darkgrey'
      }
      return 'primary'
    },

    computeTimeLeftSeconds(startedDateTime, durationInHours, totalRunningTimeInSeconds) {
      const durationInSeconds = durationInHours * 60 * 60;
      const differenceInSeconds = (startedDateTime ? ((new Date() - new Date(startedDateTime)) / 1000) : 0) + totalRunningTimeInSeconds;

      if (differenceInSeconds > durationInSeconds) {
        return Math.ceil(durationInSeconds - differenceInSeconds);
      }

      return Math.floor(durationInSeconds - differenceInSeconds);
    },

    renderTimeLeft(timeLeftSeconds) {
      if (!timeLeftSeconds && (timeLeftSeconds !== 0)) {
        return ''
      }

      let negative = timeLeftSeconds < 0;
      if (negative) {
        timeLeftSeconds = -timeLeftSeconds;
      }

      const hours = Math.floor(timeLeftSeconds / 3600);
      timeLeftSeconds = timeLeftSeconds % 3600;

      const minutes = Math.floor(timeLeftSeconds / 60);
      const seconds = timeLeftSeconds % 60;

      let timeLeftString = `${negative?'-':''}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      if (timeLeftSeconds > 24 * 60 * 60) {
        return `<span style="color:lightgreen">${timeLeftString}</span>`;
      }
      if (timeLeftSeconds > 12 * 60 * 60) {
        return `<span style="color:lightsalmon">${timeLeftString}</span>`;
      }
      return `<span style="color:lightcoral">${timeLeftString}</span>`;
    },

    formatTotalRunningTime(totalRunningTimeSeconds) {
      if (!totalRunningTimeSeconds && (totalRunningTimeSeconds !== 0)) {
        return ''
      }

      totalRunningTimeSeconds = Math.ceil(totalRunningTimeSeconds);
      const hours = Math.floor(totalRunningTimeSeconds / 3600);
      totalRunningTimeSeconds = totalRunningTimeSeconds % 3600;

      const minutes = Math.floor(totalRunningTimeSeconds / 60);
      const seconds = totalRunningTimeSeconds % 60;

      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    newParticipantItem() {
      if (!this.participantSearch || (this.participantSearch.trim() === '')) {
        return;
      }

      this.participantMenu = false;
      if (!this.isValidEmail(this.participantSearch)) {
        this.failedAddNewParticipant = true;
        return;
      }

      this.failedAddNewParticipant = false;
      this.dialogDisabled = true;
      this.addParticipant(this.participantSearch).then((response) => {
          let newParticipant = response.data;
          addItemsIfNotExists(this.participantItems, newParticipant);
          addItemsIfNotExists(this.editedItem.participants, newParticipant);
          this.participantSearch='';
      }).catch(err => {
        this.failedAddNewParticipant = true;
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false;
      });
    },

    isValidEmail(email) {
        const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return regex.test(email);
    },

    isCurrentUserParticipant(virtualPanel) {
      return virtualPanel.participants?.find(participant => participant.id === this.$store.getters.stateUser.user_id) !== undefined
    },

    isCurrentUserParticipantInAnyStartedPanel() {
      return this.filteredStartedVirtualPanelIndices.some(i => this.isCurrentUserParticipant(this.filteredVirtualPanels[i]))
    },

    answerUpdated(virtualPanelId, question) {
      let maxNumberOfCharacters = question.maximum_answer_length
      this.$nextTick(() => {
        question.answer = question.answer.substring(0, maxNumberOfCharacters)
      })
      question.answerTimer = debounce((answer, answerFile) => {
        question.failedSaveAnswer = undefined;
        question.savingAnswer = true
        this.saveAnswer(virtualPanelId, question.id, answer, answerFile)
            .then((response) => {
              if (response.data.answer_file) {
                question.answerFile = new File([], response.data.answer_file.filename, {
                  type: response.data.answer_file.content_type,
                })
              }
            })
            .catch(err => {
              if (err.response.status === 413) {
                question.failedSaveAnswer = 'Failed to save answer! ' + err.response.data.detail
              } else {
                question.failedSaveAnswer = 'Failed to save answer! You can copy the answer into another document in ' +
                    'order to salvage your changes. Then close and reopen this dialog, to make another attempt.';
              }
              console.log(err);
            }).finally(() => {
              question.savingAnswer = false;
            });
      }, question.answerTimer)(question.answer || '', question.answerFile);
    },

    isValidAnswer(questionItem) {
      return (questionItem.answer?.length >= questionItem.minimum_answer_length) &&
          (questionItem.answer?.length <= questionItem.maximum_answer_length)
    },

    computeValidAnswers(questions) {
      let count = 0;
      questions.forEach(question => {
        if (this.isValidAnswer(question)) {
          count++;
        }
      });
      return count;
    },

    allAnswersValid(questions) {
      return this.computeValidAnswers(questions) === questions.length;
    },

    populateVirtualPanelTimeLeftAndTotalRunningTimeDynamic(virtualPanelIndices, virtualPanels) {
      virtualPanelIndices.forEach(i => {
        virtualPanels[i].time_left_in_seconds = this.computeTimeLeftSeconds(
            (virtualPanels[i].status === 'STARTED') ? virtualPanels[i].started_date_time : undefined,
            virtualPanels[i].duration_in_hours,
            virtualPanels[i].total_running_time_in_seconds);
        if (virtualPanels[i].time_left_in_seconds) {
          virtualPanels[i].total_running_time_in_seconds_dynamic =
              (virtualPanels[i].duration_in_hours * 3600) - virtualPanels[i].time_left_in_seconds;
        }
      })
    },

    populateVirtualPanelCompletedAnswers(virtualPanelIndices, virtualPanels) {
      let virtualPanelIndicesByIds = new Map();
      if (virtualPanelIndices.length === 0) {
        return;
      }
      virtualPanelIndices.forEach(i => {
        virtualPanelIndicesByIds.set(virtualPanels[i].id, i)
      })
      this.getVirtualPanelCompletedAnswers([...virtualPanelIndicesByIds.keys()]).then((response) => {
        for (let key in response.data) {
          const i = virtualPanelIndicesByIds.get(key);
          virtualPanels[i].completed_answers = response.data[key];
        }
      }).catch(err => {
        console.log(err);
      });
    },

    populateVirtualPanelPartialAnswers(virtualPanelIndices, virtualPanels) {
      let virtualPanelIndicesByIds = new Map();
      if (virtualPanelIndices.length === 0) {
        return;
      }
      virtualPanelIndices.forEach(i => {
        virtualPanelIndicesByIds.set(virtualPanels[i].id, i)
      })
      this.getVirtualPanelPartialAnswers([...virtualPanelIndicesByIds.keys()]).then((response) => {
        for (let key in response.data) {
          const i = virtualPanelIndicesByIds.get(key);
          virtualPanels[i].partial_answers = response.data[key];
        }
      }).catch(err => {
        console.log(err);
      });
    },

    getAnsweringParticipants(questionId, completedOrPartialAnswers, participants) {
      if (!completedOrPartialAnswers) {
        return [];
      }

      // TODO: Inefficient. Rewrite.
      let answeredParticipants = []
      for (let userId in completedOrPartialAnswers) {
        let questionIdsAndHasAnswerFiles = completedOrPartialAnswers[userId]
        const questionIdAndHasAnswerFile = questionIdsAndHasAnswerFiles.find(q => q.questionId === questionId)
        if (questionIdAndHasAnswerFile) {
          let participant = participants.find(p => p.id === userId)
          if (participant) {
            answeredParticipants.push({participant: participant, hasAnswerFile: questionIdAndHasAnswerFile.hasAnswerFile})
          }
        }
      }

      return answeredParticipants
    },

    getCustomerLogoFile(customer) {
      if (customer?.attributes?.logo?.length === 1) {
        return customer.attributes.logo[0]
      }
      return null
    },

    getCustomerBackgroundColor(customer) {
      if (customer?.attributes?.backgroundColor?.length === 1) {
        return customer.attributes.backgroundColor[0]
      }
      return undefined
    },

    getCustomerForegroundColor(customer) {
      if (customer?.attributes?.foregroundColor?.length === 1) {
        return customer.attributes.foregroundColor[0]
      }
      return undefined
    },

    renderCustomerStyle(customer) {
      let style = '';
      let customerBackgroundColor = this.getCustomerBackgroundColor(customer);
      if (customerBackgroundColor !== undefined) {
        style += `background-color: ${customerBackgroundColor};`;
      }
      let customerForegroundColor = this.getCustomerForegroundColor(customer);
      if (customerForegroundColor !== undefined) {
        style += `color: ${customerForegroundColor};`;
      }
      return style;
    },

    renderDisabledStyle(item) {
      return ((item.status === 'DELETED') || (this.isCurrentUserParticipant(item) && (!this.ownProfileComplete
          || !this.currentUserContracted(item.group?.id, item.customer?.id)))) ? 'color:darkgrey' : '';
    },

    profileComplete(participant) {
      return participant.email?.trim().length > 0 &&
          participant.emailVerified &&
          participant.firstName?.trim().length > 0 &&
          participant.lastName?.trim().length > 0 &&
          participant.jobTitle?.trim().length > 0 &&
          participant.affiliation?.trim().length > 0 &&
          participant.phone?.trim().length > 0 &&
          participant.countryCode?.trim().length === 2 &&
          participant.cvFileAvailable
    },

    getContract(userId, group, customer) {
      return this.virtualPanelContracts.find(c => c.userId === userId && c.group === group && c.customer === customer)
    },

    getContractEndDate(userId, group, customer) {
      let contract = this.getContract(userId, group, customer)
      return contract && new Date(contract.endDate)
    },

    setContractEndDate(userId, group, customer, endDate) {
      let contract = this.getContract(userId, group, customer)
      if (contract) {
        contract.endDate = endDate
      } else {
        this.virtualPanelContracts.push({group: group, customer: customer, userId: userId, endDate: endDate})
      }
    },

    participantContracted(userId, group, customer) {
      let contractEndDate = userId && group && customer && this.getContractEndDate(userId, group, customer)
      let currentDate = new Date()
      return contractEndDate && (contractEndDate > currentDate)
    },

    currentUserContracted(group, customer) {
      return this.participantContracted(this.$store.getters.stateUser.user_id, group, customer)
    }
  }
})
</script>

<style lang="scss" scoped>
.dp__theme_dark {
  --dp-background-color: #364c5b;
  --dp-text-color: #dcdcdc;
  --dp-border-color: #dcdcdc;
}
.error {
  color: indianred;
}

@layer primevue {
  .p-progressbar {
    background-color: #364c5b;
  }
}

::v-deep .ciencia_row:hover {
  background-color: #566c7b;
}
</style>
